import { getLogger } from '@/lib/logger';
import type { AuthUser } from '@/types';
import { exchangePlatformService } from '@/services/platform-service';
import {
  extractAndValidatePlatformFromUrl,
  extractAndValidateTokenFromUrl,
} from '@/utils/url-token-extractor';
import { sessionHintService } from './session-hint-service';
import { tokenRefreshManager } from './token-refresh-manager';
import { tokenService } from './token-service';

const logger = getLogger({ scope: 'auth.auth-service' });

/** Default session expiry (5 minutes) when /auth/me doesn't provide expiresIn */
const DEFAULT_SESSION_EXPIRY_SECONDS = 5 * 60;

interface AuthResult {
  success: boolean;
  error?: string;
  user?: AuthUser;
  expiresIn?: number;
  /** True when the user had an active session that could not be recovered (expired/invalid cookie). */
  sessionExpired?: boolean;
}

/**
 * Service responsible for authentication business logic.
 * Handles token validation, exchange, and session recovery.
 * No localStorage — tokens are managed via HttpOnly cookies.
 */
export class AuthService {
  /**
   * @deprecated Remove this when login link flow is fully implemented
   *
   * 1. Check the token in the URL
   * 2. Check the platform in the URL
   * 3. Call the Exchange token API
   */
  static async validateAuthUrl(): Promise<AuthResult> {
    try {
      const urlTokenResult = extractAndValidateTokenFromUrl();

      if (!urlTokenResult.token || !urlTokenResult.isValid) {
        return { success: false, error: 'Invalid or missing token' };
      }

      const platformResult = extractAndValidatePlatformFromUrl((platform: string) =>
        exchangePlatformService.validatePlatform(platform)
      );

      if (!platformResult.platform || !platformResult.isValid) {
        return { success: false, error: 'Missing or invalid platform parameter' };
      }

      return this.exchangeToken(urlTokenResult.token, platformResult.platform!);
    } catch (error) {
      logger.error('Error validating auth URL', { error });
      return { success: false, error: 'Invalid or missing token' };
    }
  }

  /**
   * Initialize authentication using the following recovery chain:
   * 1. Try URL token exchange (SSO flow)
   * 2. Try GET /auth/me (existing cookie session)
   * 3. Try POST /auth/refresh, then GET /auth/me again
   * 4. If all fail, return error
   */
  static async initialize(): Promise<AuthResult> {
    try {
      // This code/logic is duplicated on Purpose, this will be removed soon, when we clean the legacy SSO auth
      // We didn't reuse the extractAndValidateTokenFromUrl because it removes the token from URL which could cause error login attempts
      const rawSearch = new URLSearchParams(window.location.search);
      const hadUrlToken = rawSearch.has('auth') || rawSearch.has('authToken');

      // Step 1: Try URL token exchange
      const urlValidation = await this.validateAuthUrl();

      if (urlValidation.success) {
        if (urlValidation.expiresIn) {
          tokenRefreshManager.setExpiresIn(urlValidation.expiresIn);
        }
        tokenRefreshManager.startAutoRefresh();
        sessionHintService.set();
        return urlValidation;
      }

      // If a URL token was present but failed, surface the error immediately —
      // do not attempt cookie-based recovery for an explicit (but invalid) token flow.
      if (hadUrlToken) {
        return urlValidation;
      }

      // No prior session hint — skip recovery to avoid misleading "session expired" on first visit
      const hadPreviousSession = sessionHintService.exists();
      if (!hadPreviousSession) {
        return { success: false };
      }

      // Step 2: Try existing session via /auth/me
      try {
        const user = await tokenService.fetchCurrentUser();
        tokenRefreshManager.setExpiresIn(DEFAULT_SESSION_EXPIRY_SECONDS);
        tokenRefreshManager.startAutoRefresh();
        sessionHintService.set();
        return { success: true, user };
      } catch {
        // /auth/me failed (likely 401), try refreshing
      }

      // Step 3: Try refresh, then /auth/me again
      try {
        const refreshResponse = await tokenService.refreshAuthToken();
        tokenRefreshManager.setExpiresIn(refreshResponse.expiresIn);

        const user = await tokenService.fetchCurrentUser();
        tokenRefreshManager.startAutoRefresh();
        sessionHintService.set();
        return { success: true, user, expiresIn: refreshResponse.expiresIn };
      } catch {
        // Refresh also failed — session could not be recovered
        return { success: false, sessionExpired: true };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication initialization failed',
      };
    }
  }

  /**
   * Exchange a platform token for auth cookies
   */
  static async exchangeToken(token: string, platform: string): Promise<AuthResult> {
    try {
      const authResponse = await tokenService.exchangeToken(token, platform);

      return { success: true, user: authResponse.user, expiresIn: authResponse.expiresIn };
    } catch (exchangeError) {
      logger.error('Token exchange failed', { error: exchangeError });
      return {
        success: false,
        error: exchangeError instanceof Error ? exchangeError.message : 'Token exchange failed',
      };
    }
  }
}
